import { Descendant, EditorVersion } from '../../editor/types'
import { TemplateExternalPdf } from '../templates'
import { FieldMapValue } from '../utils/common'
import { ConsentForm } from './consentForms'

// A prior sent/signed version of a practice contract, kept when a contract is re-sent.
export interface PracticeContractVersion {
  formData: FieldMapValue | null
  toBeSignedByUser: string
  sentOn: number
  sentBy: string
  signedOn: number | null
  signedBy: string | null
  signedStoragePath: string | null
  text?: Descendant[] | null
  editorVersion?: EditorVersion
  viewedOn?: number | null
}

// A contract sent to a specific super-admin provider of a practice, to be signed in providers-app.
// Mirrors ConsentForm, but is addressed to a provider + practice rather than a patient + assessment.
// Stored at midwives/{practiceId}/practice-contracts/{contractId}.
export interface PracticeContract {
  type: 'practiceContract'
  name: string
  practiceId: string
  practiceContractTemplateId: string
  // the provider uid the contract is addressed to (and the only user allowed to sign it)
  toBeSignedByUser: string
  formData: FieldMapValue | null
  text: Descendant[] | null
  isExternalPdf?: boolean
  externalPdf: TemplateExternalPdf | null
  editorVersion?: EditorVersion
  sentOn: number
  sentBy: string
  signedOn: number | null
  signedBy: string | null
  signedStoragePath: string | null
  signedOffSite?: boolean
  viewedOn?: number | null
  previousVersions?: PracticeContractVersion[]
}

export const isPracticeContract = (doc: { type?: string } | null): doc is PracticeContract =>
  !!doc && doc.type === 'practiceContract'

// A document signed in-app via the shared DocumentView / DocumentFormProvider machinery.
// Consent forms (patient-signed) and practice contracts (provider-signed) share the same shape
// for signing purposes, so the render layer gates on this rather than on isConsentForm alone.
export type SignableDocument = ConsentForm | PracticeContract

export const isSignableDocument = (
  doc: { type?: string } | null | undefined,
): doc is SignableDocument =>
  !!doc && (doc.type === 'consentForm' || doc.type === 'practiceContract')
